// Pure, dependency-free helpers for reading and immutably writing values at a
// content path inside an LP Express page's `generatedContent`, plus a leaf
// guard that prevents a scalar (text/link) write from clobbering a whole
// object/array branch.
//
// These are shared verbatim by the client optimistic editor
// (client/src/pages/landing-pages-express.tsx) and the server persistence
// routes (server/routes.ts) so optimistic edits and the persisted edit operate
// on identical path semantics. Extracted here so the contract has a single
// source of truth and can be unit-tested without a browser/React render or a
// live Express/Postgres boot. `server/__tests__/lpDeepSet.test.ts` locks the
// behavior in.

// Read the value at a content path (used to locate an item array before a
// hide/delete op). Returns undefined if any segment is missing.
export function lpGetAt(obj: any, path: (string | number)[]): any {
  let cur = obj;
  for (const seg of path) {
    if (cur == null) return undefined;
    cur = cur[seg as any];
  }
  return cur;
}

// Immutably set a value at a content path, cloning each container along the way
// so the original generatedContent object is never mutated in place.
export function lpDeepSet(obj: any, path: (string | number)[], value: any): any {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (typeof head === "number") {
    const arr = Array.isArray(obj) ? [...obj] : [];
    arr[head] = lpDeepSet(arr[head], rest, value);
    return arr;
  }
  const next = obj && typeof obj === "object" && !Array.isArray(obj) ? { ...obj } : {};
  next[head] = lpDeepSet(next[head], rest, value);
  return next;
}

// Guard a scalar (text/link) write so it can only ever land on a LEAF — never
// clobber a whole object/array branch. A path that resolves to an object or
// array means the field's `path` doesn't map cleanly to a single leaf (a
// path/state mismatch); writing a string there would wipe every sibling field
// in that branch. We also require at least a root key + one segment so a write
// can never replace an entire top-level content key. Returns true when the
// write is safe to apply.
export function lpCanSetLeaf(obj: any, path: (string | number)[]): boolean {
  if (!Array.isArray(path) || path.length < 2) return false;
  // Every intermediate node must be missing or a real container (object/array).
  // If an ancestor is a scalar, writing deeper would replace that scalar with a
  // synthetic object and lose its original value.
  let cur = obj;
  for (let i = 0; i < path.length - 1; i++) {
    if (cur == null) break; // missing ancestor — lpDeepSet will create it safely
    if (typeof cur !== "object") return false; // scalar ancestor — unsafe
    cur = cur[path[i] as any];
  }
  if (cur != null && typeof cur !== "object") {
    // Parent is a scalar (e.g. path goes one level too deep) — unsafe.
    return false;
  }
  const leaf = cur == null ? undefined : cur[path[path.length - 1] as any];
  // Only empty/scalar leaves may be overwritten. Existing object/array = branch.
  return (
    leaf == null ||
    typeof leaf === "string" ||
    typeof leaf === "number" ||
    typeof leaf === "boolean"
  );
}
